import type {
  MetricCatalogMetricEntry,
  MetricCatalogModuleEntry,
  MetricCatalogResponse,
  MetricCatalogResponseEntry,
  MetricCatalogScoreTypeEntry,
  MetricCustomization,
} from "@/interfaces/v2Analytics";
import {
  labelVariants,
  panelTextVariants,
  type MarketTextVariant,
} from "@/utils/catalogLabels";
import { isAiVisibilityModule } from "@/utils/aiVisibility/isAiVisibilityModule";

/**
 * Defensive: config.module is always "IMAGERY" for the emotional screen
 * (emotional.config.ts:46), but userSelection.module can carry "EMOTIONAL";
 * normalize so any caller vocabulary works. Do not write a test that tries to
 * reach this via config.module — it can't.
 *
 * Same idea for AI Visibility: `screenConfig.module` carries "AI_VISIBILITY"
 * (aivisibility.config.ts), which lowercases to "ai_visibility" -- the
 * catalog's own moduleKey -- but `ViewDefinition.navigation.module` (the
 * stored/URL vocabulary, see viewDefinition/types.ts's ViewModule) spells it
 * "aivisibility", no underscore. Without this alias, a caller using the
 * stored spelling (e.g. the view-details panel's metricLabel resolver) can
 * never find the module's catalog entry, even though the same metric
 * resolves fine wherever screenConfig.module is used instead.
 */
function normalizeModuleKey(module: string): string {
  const m = module.toLowerCase();
  if (m === "emotional") return "imagery";
  // App / screenConfig: AI_VISIBILITY; ViewDefinition: aivisibility;
  // catalog moduleKey: ai_visibility.
  if (isAiVisibilityModule(module)) return "ai_visibility";
  return m;
}

export function getModuleEntry(
  catalog: MetricCatalogResponse | null,
  module: string,
): MetricCatalogModuleEntry | undefined {
  if (!catalog || !module) return undefined;
  const key = normalizeModuleKey(module);
  return catalog.modules.find((m) => m.moduleKey.toLowerCase() === key);
}

/**
 * The module's entry for `metricKey`: one of its `metrics`, else one of its
 * `standardMetrics`, so a standard key still resolves on a module that shows the
 * account's own funnel steps.
 */
export function getMetricEntry(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null | undefined,
): MetricCatalogMetricEntry | undefined {
  if (!metricKey) return undefined;
  const key = metricKey.toLowerCase();
  const entry = getModuleEntry(catalog, module);
  const hasKey = (m: MetricCatalogMetricEntry) => m.metricKey.toLowerCase() === key;
  return entry?.metrics.find(hasKey) ?? entry?.standardMetrics?.find(hasKey);
}

/**
 * The markets whose opinion on this metric's wording actually counts: the
 * selection intersected with the markets that DEFINE the metric.
 *
 * Without this, a selected market that does not offer the metric contributes
 * the canonical value (it has no override) and manufactures a false conflict.
 * An empty intersection resolves canonically.
 */
function relevantMarkets(
  entry: MetricCatalogMetricEntry,
  marketIds: number[],
): number[] {
  // Runtime catalogs (esp. stale bootstrap cache) can omit marketIds.
  return marketIds.filter((id) => (entry.marketIds ?? []).includes(id));
}

/**
 * Per-market variants of this metric's display label across the selection.
 * One variant → every relevant market agrees. Several → genuine divergence.
 * `[]` when the module/metric is not in the catalog.
 */
export function getMetricLabelVariants(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null,
  marketIds: number[],
): MarketTextVariant[] {
  const entry = getMetricEntry(catalog, module, metricKey);
  if (!entry) return [];
  return labelVariants(entry, relevantMarkets(entry, marketIds));
}

/**
 * Per-market variants of this metric's question wording (falling back to its
 * description) across the selection. One variant → every relevant market was
 * asked the same thing. Several → they were asked different things.
 * `[]` when the module/metric is not in the catalog.
 */
export function getMetricPanelTextVariants(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null,
  marketIds: number[],
): MarketTextVariant[] {
  const entry = getMetricEntry(catalog, module, metricKey);
  if (!entry) return [];
  return panelTextVariants(entry, relevantMarkets(entry, marketIds));
}

function filterByMarkets(
  metrics: MetricCatalogMetricEntry[],
  marketIds: number[],
): MetricCatalogMetricEntry[] {
  if (marketIds.length === 0) return metrics;
  // Missing / empty marketIds = unknown applicability (stale bootstrap catalog).
  // Treat as available in all markets so selection + data fetches are not blocked.
  return metrics.filter((m) => {
    const ids = m.marketIds;
    if (ids == null || ids.length === 0) return true;
    return ids.some((id) => marketIds.includes(id));
  });
}

/** A catalog without `standardMetrics` lists its standard metrics as `metrics`. */
function standardMetricsOf(entry: MetricCatalogModuleEntry): MetricCatalogMetricEntry[] {
  return entry.standardMetrics ?? entry.metrics;
}

/**
 * The entries a module serves for a selection. A module with the account's own
 * entries (a customized conversion funnel) serves them only when the selection's
 * first market is one they are defined in, the rule the backend answers the
 * request by; a selection led by any other market gets the standard metrics.
 */
function servedEntries(
  entry: MetricCatalogModuleEntry,
  marketIds: number[],
): MetricCatalogMetricEntry[] {
  const lead = marketIds[0];
  if (lead == null || !entry.customization?.customized) return entry.metrics;
  return filterByMarkets(entry.metrics, [lead]).length > 0
    ? entry.metrics
    : standardMetricsOf(entry);
}

/**
 * Catalog-ordered metrics the module serves for the selection (see
 * `servedEntries`), narrowed to those defined in ≥1 selected market. Empty
 * selection → all of the module's entries.
 */
export function getModuleMetrics(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): MetricCatalogMetricEntry[] {
  const entry = getModuleEntry(catalog, module);
  if (!entry) return [];
  return filterByMarkets(servedEntries(entry, marketIds), marketIds);
}

/**
 * The account's own conversion funnel steps the selection is served, or
 * `undefined` when the selection gets the standard conversion measures.
 */
export function getCustomFunnelSteps(
  catalog: MetricCatalogResponse | null | undefined,
  marketIds: number[],
): MetricCatalogMetricEntry[] | undefined {
  const entry = getModuleEntry(catalog ?? null, "conversion");
  if (!entry?.customization?.customized) return undefined;
  const metrics = getModuleMetrics(catalog ?? null, "conversion", marketIds);
  return metrics.some((m) => m.sourceMetricKey) ? metrics : undefined;
}

/**
 * The conversion module's metrics by their standard key, even for an account
 * whose conversion `metrics` are its own funnel steps. Market-filtered by the
 * same rule as `getModuleMetrics`.
 */
export function getConversionStandardMetrics(
  catalog: MetricCatalogResponse | null | undefined,
  markets: number[] = [],
): MetricCatalogMetricEntry[] {
  const entry = getModuleEntry(catalog ?? null, "conversion");
  return filterByMarkets(entry ? standardMetricsOf(entry) : [], markets);
}

/**
 * The standard metrics of a module the AI Visibility human-metric picker reads:
 * conversion through its standard metrics, so an account's funnel steps never
 * shadow them; any other module through its metrics. Market-filtered like
 * `getModuleMetrics`; empty `markets` → all.
 */
export function getHumanMetricSourceMetrics(
  catalog: MetricCatalogResponse | null | undefined,
  module: string,
  markets: number[] = [],
): MetricCatalogMetricEntry[] {
  return module.toLowerCase() === "conversion"
    ? getConversionStandardMetrics(catalog, markets)
    : getModuleMetrics(catalog ?? null, module, markets);
}

/**
 * The other key of a funnel step pair: a step's standard source metric, or the
 * step measured from a standard metric. `undefined` when the key has none.
 */
export function getMetricKeyCounterpart(
  catalog: MetricCatalogResponse | null | undefined,
  module: string,
  metricKey: string,
): string | undefined {
  const steps = getModuleEntry(catalog ?? null, module)?.metrics ?? [];
  return (
    steps.find((m) => m.metricKey === metricKey)?.sourceMetricKey ??
    steps.find((m) => m.sourceMetricKey === metricKey)?.metricKey
  );
}

/**
 * The key the selection lists for `metricKey`: the key itself, else its funnel
 * step pair (see `getMetricKeyCounterpart`). `undefined` when neither is listed.
 */
export function resolveListedMetricKey(
  catalog: MetricCatalogResponse | null | undefined,
  module: string,
  metricKey: string,
  markets: number[] = [],
): string | undefined {
  const listed = getModuleMetrics(catalog ?? null, module, markets).map((m) => m.metricKey);
  if (listed.includes(metricKey)) return metricKey;
  const counterpart = getMetricKeyCounterpart(catalog, module, metricKey);
  return counterpart && listed.includes(counterpart) ? counterpart : undefined;
}

/**
 * The module metric a screen's configured default trend metric stands for: the
 * key the selection lists for it (see `resolveListedMetricKey`), else the
 * module's first metric. `undefined` when the module lists none.
 */
export function resolveDefaultTrendStep(
  catalog: MetricCatalogResponse | null | undefined,
  module: string,
  configuredDefault: string,
  markets: number[] = [],
): string | undefined {
  return (
    resolveListedMetricKey(catalog, module, configuredDefault, markets) ??
    getModuleMetrics(catalog ?? null, module, markets)[0]?.metricKey
  );
}

/**
 * The customization of a metric or funnel step that uses the account's own
 * definition for the selection; its `description`, when present, is the rule
 * in plain words. `undefined` for a standard metric or one not in the catalog.
 */
export function getMetricCustomization(
  catalog: MetricCatalogResponse | null | undefined,
  module: string,
  metricKey: string | null | undefined,
  marketIds: number[] = [],
): MetricCustomization | undefined {
  const metric = getMetricEntry(catalog ?? null, module, metricKey);
  return activeCustomization(metric?.customization, metric?.marketIds, marketIds);
}

/**
 * A customization that applies to the selection. The backend counts a whole
 * request by its first market's definitions, so it applies when that market is
 * one it lists (no list → every market the entry is defined in). Applies to an
 * empty selection.
 */
function activeCustomization(
  customization: MetricCustomization | undefined,
  entryMarketIds: number[] | undefined,
  marketIds: number[],
): MetricCustomization | undefined {
  if (!customization?.customized) return undefined;
  const lead = marketIds[0];
  const scope = customization.marketIds ?? entryMarketIds;
  if (lead == null || !scope?.length) return customization;
  return scope.includes(lead) ? customization : undefined;
}

/** Insertion order IS menu order — never sort. */
export function getScoreTypeOptions(
  entry: MetricCatalogMetricEntry | undefined,
): { key: string; entry: MetricCatalogScoreTypeEntry }[] {
  if (!entry?.scoreTypes) return [];
  return Object.entries(entry.scoreTypes).map(([key, e]) => ({ key, entry: e }));
}

/**
 * Union across the module's market-eligible metrics, first-seen order (drives
 * the PIT method menu). Market-filtered by the same rule as `getModuleMetrics`:
 * a metric counts when it is offered in ≥1 selected market; empty selection →
 * all metrics.
 */
export function getModuleScoreTypeOptions(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): { key: string; entry: MetricCatalogScoreTypeEntry }[] {
  const seen = new Map<string, MetricCatalogScoreTypeEntry>();
  for (const metric of getModuleMetrics(catalog, module, marketIds)) {
    for (const [key, e] of Object.entries(metric.scoreTypes ?? {})) {
      if (!seen.has(key)) seen.set(key, e);
    }
  }
  return Array.from(seen, ([key, entry]) => ({ key, entry }));
}

/**
 * Score Type control renders iff true (derived, never configured). Also gates
 * module-level response logic: single-method modules never carry a response in
 * PIT/PRE_POST — per-metric catalog defaults apply server-side.
 */
export function moduleHasMultipleMethods(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): boolean {
  return getModuleScoreTypeOptions(catalog, module, marketIds).length > 1;
}

/**
 * Module landing default = the FIRST market-eligible catalog metric's
 * defaultScoreType (reputation → QRQ → MEAN; reputation in market 40, where only
 * `quality` is offered → PERCENTAGE).
 */
export function getModuleDefaultScoreType(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): string | null {
  return getModuleMetrics(catalog, module, marketIds)[0]?.defaultScoreType ?? null;
}

export function getResponses(
  entry: MetricCatalogMetricEntry | undefined,
  scoreType: string | null,
): MetricCatalogResponseEntry[] | undefined {
  if (!entry || !scoreType || !entry.scoreTypes) return undefined;
  return entry.scoreTypes[scoreType]?.responses;
}

export function getDefaultResponse(
  entry: MetricCatalogMetricEntry | undefined,
  scoreType: string | null,
): string | null {
  if (!entry || !scoreType || !entry.scoreTypes) return null;
  return entry.scoreTypes[scoreType]?.defaultResponse ?? null;
}

/**
 * PIT/Pre-Post response menu: the first module metric carrying responses under
 * the method. Module AGGREGATE vocabularies are uniform today; assert in dev so
 * a future divergent catalog fails loudly instead of rendering a wrong menu.
 */
export function getModuleResponses(
  catalog: MetricCatalogResponse | null,
  module: string,
  scoreType: string | null,
  marketIds: number[],
): MetricCatalogResponseEntry[] | undefined {
  if (!scoreType) return undefined;
  const metrics = getModuleMetrics(catalog, module, marketIds);
  const carriers = metrics
    .map((m) => m.scoreTypes?.[scoreType]?.responses)
    .filter((r): r is MetricCatalogResponseEntry[] => r != null);
  if (carriers.length === 0) return undefined;
  if (import.meta.env.DEV) {
    const key = (rs: MetricCatalogResponseEntry[]) =>
      rs.filter((r) => r.kind === "AGGREGATE").map((r) => r.response).join(",");
    const first = key(carriers[0]);
    if (carriers.some((rs) => key(rs) !== first)) {
      const msg = `[metricsCatalog] module "${module}" metrics disagree on AGGREGATE responses under ${scoreType}`;
      // Vitest runs with DEV truthy: throw under test so a divergent catalog
      // fails loudly; log-only in the running app.
      if (import.meta.env.MODE === "test") throw new Error(msg);
      // eslint-disable-next-line no-console
      console.error(msg);
    }
  }
  return carriers[0];
}

export function getModuleDefaultResponse(
  catalog: MetricCatalogResponse | null,
  module: string,
  scoreType: string | null,
  marketIds: number[],
): string | null {
  if (!scoreType) return null;
  for (const m of getModuleMetrics(catalog, module, marketIds)) {
    const d = m.scoreTypes?.[scoreType]?.defaultResponse;
    if (d != null) return d;
  }
  return null;
}

// The per-market label rule lives in `@/utils/catalogLabels` — the distribution
// catalog carries the same wire shape and resolves through the same function.
